/**
 * Vyrtual-managed venues: the public showcase, the intake wizard, and the pipeline.
 *
 * Distinct from lib/venue.ts, which is the self-serve venue portal. A managed room is
 * operated by Vyrtual, priced from a rate card, and booked through an eight step intake
 * that is open to organizers with no account.
 *
 * Every price shown by the wizard is a PREVIEW. The engine recalculates from the rate card
 * on submit and stores its own figure, so the sidebar is there to inform a decision, never
 * to determine what is charged.
 */

import { api } from './apiClient';

export interface ManagedVenue {
  id: string;
  name: string;
  slug: string;
  address: string;
  city: string;
  state: string;
  capacity: number;
  managingEntity: string;
  stageWidthFt: number | null;
  stageDepthFt: number | null;
  loadInNotes: string | null;
  powerSpecs: string | null;
  photos: string[];
}

export interface RateCard {
  baseRentalCents: number;
  baseHours: number;
  extraHourRateCents: number;
  cleaningFeeCents: number;
  techBaseRateCents: number;
  techExtraHourRateCents: number;
  ledWallPriceCents: number;
  projectorPriceCents: number;
  schoolFeePct: number;
  vyrtualFeePct: number;
}

export type ManagedVenueDetail = ManagedVenue & { rateCard: RateCard };

export const BROADCAST_PRICES = { basic: 50_000, standard: 180_000, pro: 300_000 } as const;
export const MARKETING_PRICES = { awareness: 30_000, boosted: 75_000, campaign: 150_000 } as const;
export const STRIPE_PCT = 0.029;
export const STRIPE_FIXED_CENTS = 30;

export type BroadcastPackage = keyof typeof BROADCAST_PRICES;
export type MarketingPackage = keyof typeof MARKETING_PRICES;

export interface BookingDraft {
  organizerName: string;
  organizerEmail: string;
  eventName: string;
  eventType: string;
  eventStartDate: string;
  eventEndDate: string;
  attendance: number;
  extraHours: number;
  admissionType: 'free' | 'ticketed';
  ticketPriceCents: number;
  rsvpCap: number;
  technicianCount: number;
  broadcastPackage: BroadcastPackage | null;
  ledWall: boolean;
  projector: boolean;
  marketingPackage: MarketingPackage | null;
  inputList: string;
  stagePlotUrl: string;
  coiUrl: string;
  coiCoverageDays: number;
  exclusivityAgreed: boolean;
}

export interface CostLine { key: string; label: string; amountCents: number }

/**
 * The sidebar total.
 *
 * Mirrors computeOrganizerCost in the engine deliberately, so the figure a organizer
 * watches while filling the form is the figure they are quoted. The engine's version is
 * authoritative; this one exists so the number can move without a round trip on every
 * keystroke.
 */
export function previewCost(draft: BookingDraft, rate: RateCard): {
  lines: CostLine[]; totalCents: number;
} {
  const lines: CostLine[] = [
    { key: 'base_rental', label: `Facility rental, ${rate.baseHours} hours`, amountCents: rate.baseRentalCents },
  ];
  if (draft.extraHours > 0) {
    lines.push({
      key: 'extra_hours',
      label: `Extended hours (${draft.extraHours})`,
      amountCents: draft.extraHours * rate.extraHourRateCents,
    });
  }
  lines.push({ key: 'cleaning', label: 'Cleaning', amountCents: rate.cleaningFeeCents });
  lines.push({
    key: 'technicians',
    label: `Technicians (${draft.technicianCount})`,
    amountCents: draft.technicianCount * rate.techBaseRateCents,
  });
  if (draft.extraHours > 0) {
    lines.push({
      key: 'tech_extra_hours',
      label: 'Technician extended hours',
      amountCents: draft.technicianCount * draft.extraHours * rate.techExtraHourRateCents,
    });
  }
  if (draft.broadcastPackage) {
    lines.push({
      key: 'broadcast',
      label: `Broadcast, ${draft.broadcastPackage}`,
      amountCents: BROADCAST_PRICES[draft.broadcastPackage],
    });
  }
  if (draft.ledWall) lines.push({ key: 'led_wall', label: 'LED wall', amountCents: rate.ledWallPriceCents });
  if (draft.projector) lines.push({ key: 'projector', label: 'Projector', amountCents: rate.projectorPriceCents });
  if (draft.marketingPackage) {
    lines.push({
      key: 'marketing',
      label: `Marketing, ${draft.marketingPackage}`,
      amountCents: MARKETING_PRICES[draft.marketingPackage],
    });
  }
  return { lines, totalCents: lines.reduce((t, l) => t + l.amountCents, 0) };
}

/** The earnings estimator. Same arithmetic the engine settles with. */
export function previewSettlement(
  { ticketPriceCents, ticketCount, rate }:
  { ticketPriceCents: number; ticketCount: number; rate: RateCard },
) {
  const grossCents = Math.max(0, ticketPriceCents) * Math.max(0, ticketCount);
  const schoolFeeCents = Math.round(grossCents * (rate.schoolFeePct / 100));
  const vyrtualFeeCents = Math.round(grossCents * (rate.vyrtualFeePct / 100));
  const stripeFeeCents = Math.round(grossCents * STRIPE_PCT) + ticketCount * STRIPE_FIXED_CENTS;
  const totalFeesCents = schoolFeeCents + vyrtualFeeCents + stripeFeeCents;
  return {
    grossCents,
    schoolFeeCents,
    vyrtualFeeCents,
    stripeFeeCents,
    totalFeesCents,
    netCents: Math.max(0, grossCents - totalFeesCents),
  };
}

/** How full the room is, in words. Purely descriptive. */
export function houseLabel(count: number, capacity: number): string {
  if (count <= 0) return 'Empty house';
  const pct = count / capacity;
  if (count >= capacity) return 'Full house, sold out';
  if (pct >= 0.7) return 'Strong house';
  if (pct >= 0.35) return 'Building house';
  return 'Light house';
}

export interface ManagedBooking {
  id: string;
  venueId: string;
  organizerEmail: string;
  organizerName: string;
  eventName: string;
  eventType: string;
  eventStartDate: string;
  eventEndDate: string;
  attendance: number;
  extraHours: number;
  admissionType: 'free' | 'ticketed';
  ticketPriceCents: number;
  rsvpCap: number;
  technicianCount: number;
  broadcastPackage: string | null;
  ledWall: boolean;
  projector: boolean;
  marketingPackage: string | null;
  coiUrl: string | null;
  coiCoverageDays: number;
  exclusivityAgreed: boolean;
  organizerCostCents: number;
  netPayoutCents: number;
  pipelineStage: number;
  pipelineStageLabel: string | null;
  status: string;
  hasAgreement: boolean;
  signedByName: string | null;
  signedAt: string | null;
  paymentCompletedAt: string | null;
  voletoEventId: string | null;
  fmxScheduleId: string | null;
  fmxSyncedAt: string | null;
  /** Ticket proceeds sit in Voleto's balance until a human disburses them. */
  payoutStatus: 'not_applicable' | 'pending_manual_disbursement' | 'disbursed';
  payoutReference: string | null;
  payoutCompletedAt: string | null;
  createdAt: string;
  venueName?: string;
  daysUntil?: number;
}

export const PIPELINE_STAGES = [
  'Request submitted',
  'Venue owner review',
  'Approved and scheduled',
  'Technical prep',
  'Event day',
  'Completed',
] as const;

export const listManagedVenues = () => api.get<ManagedVenue[]>('/managed-venues');
export const getManagedVenue = (slug: string) => api.get<ManagedVenueDetail>(`/managed-venues/${slug}`);

/**
 * The admin read, BY ID.
 *
 * The public read above is by slug and answers only for is_public venues, so the console
 * could not load the rate card of a privately operated venue — which is what the real
 * venue is. Admin-gated on the engine.
 */
export const adminGetManagedVenue = (venueId: string) =>
  api.get<ManagedVenueDetail>(`/admin/managed-venues/${venueId}`);

export interface Availability { year: number; month: number; blocked: Array<{ date: string; type: string }> }
export const getAvailability = (slug: string, year: number, month: number) =>
  api.get<Availability>(`/managed-venues/${slug}/availability?year=${year}&month=${month}`);

export const submitBooking = (slug: string, draft: BookingDraft) =>
  api.post<ManagedBooking>(`/managed-venues/${slug}/bookings`, draft);

export const getBooking = (id: string) => api.get<ManagedBooking>(`/managed-venues/bookings/${id}`);

export const signAgreement = (id: string, signedByName: string) =>
  api.post<ManagedBooking>(`/managed-venues/bookings/${id}/sign`, { signedByName });

/** Phase one: open a PaymentIntent and get a client secret to confirm the card with. */
export const openPaymentIntent = (id: string) =>
  api.post<{ paymentIntentId: string; clientSecret: string; amountCents: number }>(
    `/managed-venues/bookings/${id}/pay`, {});

/** Phase two: hand back the confirmed intent so the engine can verify it with Stripe. */
export const confirmPayment = (id: string, paymentIntentId: string) =>
  api.post<ManagedBooking>(`/managed-venues/bookings/${id}/pay`, { paymentIntentId });

// ── Admin console ───────────────────────────────────────────────────────────

export const adminListBookings = (params: Record<string, string> = {}) =>
  api.get<ManagedBooking[]>(`/admin/managed-venues/bookings?${new URLSearchParams(params)}`);

export const adminInsuranceAudit = (days = 30) =>
  api.get<ManagedBooking[]>(`/admin/managed-venues/insurance-audit?days=${days}`);

export interface CalendarBlock {
  id: string; venueId: string; venueName: string; bookingId: string | null;
  date: string; type: string; title: string;
  organizerName: string | null; bookingStatus: string | null;
}

export const adminCalendar = (params: Record<string, string> = {}) =>
  api.get<CalendarBlock[]>(`/admin/managed-venues/calendar?${new URLSearchParams(params)}`);

export const adminCreateBlock = (input: {
  venueId: string; date: string; blockType: string; title: string;
}) => api.post<{ id: string }>('/admin/managed-venues/calendar-blocks', input);

export const adminDeleteBlock = (id: string) =>
  api.delete<{ deleted: boolean }>(`/admin/managed-venues/calendar-blocks/${id}`);

export const adminUpdateStatus = (id: string, input: { status?: string; pipelineStage?: number }) =>
  api.patch<ManagedBooking>(`/admin/managed-venues/bookings/${id}/status`, input);

export const adminSettle = (id: string, finalTicketCount: number) =>
  api.post<ManagedBooking>(`/admin/managed-venues/bookings/${id}/settle`, { finalTicketCount });

export const adminResyncFmx = (id: string) =>
  api.post<{ synced: boolean; reason?: string }>(`/admin/managed-venues/bookings/${id}/resync-fmx`, {});

export const adminUpdateRateCard = (venueId: string, updates: Partial<RateCard>) =>
  api.patch<RateCard>('/admin/managed-venues/rate-card', { venueId, ...updates });

export const adminPendingPayouts = () =>
  api.get<ManagedBooking[]>('/admin/managed-venues/pending-payouts');

export const adminRecordPayout = (id: string, reference: string) =>
  api.post<ManagedBooking>(`/admin/managed-venues/bookings/${id}/payout`, { reference });
